import { SearchForAddressByZipCodeContent } from 'fetcher';
import { FormikProps } from 'formik';
import {
  AssortedDrillDownPropertiesGroup,
  convertValuesForDrillDownFromAssortedPropertiesGroup,
} from 'models';
import {
  FormPage,
  FormPageInputData,
  FormPageInputItem,
  FormPageInputItemSingleSelectionPulldown,
  FormPageInputItemTextSingleLine,
  FormPageProperties,
  FormPageProperty,
  INPUT_ITEM_TYPE,
  PROPERTY_TYPE,
  SingleSelectionPropertyPulldown,
  SINGLE_SELECTION_PROPERTY_INPUT_TYPE,
  TextPropertyZipCodeWithoutHyphen,
} from 'shared';

export type AutoCompleteAddressPayload = {
  addressData: SearchForAddressByZipCodeContent;
  formikProps: FormikProps<FormPageInputData>;
  searchSetting: Required<
    TextPropertyZipCodeWithoutHyphen['inputFormat']
  >['searchSetting'];
  formPage: FormPage | null;
  assortedDrillDownPropertiesGroup?: AssortedDrillDownPropertiesGroup;
};

const getAddressTextSingleLineInputItem = (
  values: FormPageInputData,
  address: string,
  addressId: string
): FormPageInputItemTextSingleLine | null => {
  const inputItem = values[addressId];
  if (inputItem?.inputItemType === INPUT_ITEM_TYPE.textSingleLine) {
    return {
      ...inputItem,
      text: address,
    };
  }
  return null;
};

const getPrefectureInputItemTextSingleLine = (
  inputItem: FormPageInputItemTextSingleLine,
  address: string
): FormPageInputItemTextSingleLine | null => {
  return {
    ...inputItem,
    text: address,
  };
};

const isPropertySingleSelectionPulldown = (
  property: FormPageProperty | undefined
): property is SingleSelectionPropertyPulldown => {
  if (property?.propertyType !== PROPERTY_TYPE.singleSelection) {
    return false;
  }
  return (
    property.inputFormat.inputType ===
    SINGLE_SELECTION_PROPERTY_INPUT_TYPE.pulldown
  );
};

const getPrefectureInputItemSingleSelectionPulldown = (
  inputItem: FormPageInputItemSingleSelectionPulldown,
  address: string,
  property: FormPageProperty
): FormPageInputItemSingleSelectionPulldown | null => {
  if (!isPropertySingleSelectionPulldown(property)) {
    return null;
  }
  const { candidates } = property;
  const value = candidates.find(
    (candidate) => candidate.label === address
  )?.value;
  if (value == null) {
    return null;
  }
  return {
    ...inputItem,
    value,
  };
};

type GetPrefectureInputItemPayload = {
  values: FormPageInputData;
  addressId: string;
  properties: FormPageProperties;
  address: {
    textSingleLine: string;
    singleSelectionPulldown: string;
  };
};
const getPrefectureInputItem = ({
  values,
  addressId,
  properties,
  address,
}: GetPrefectureInputItemPayload):
  | FormPageInputItemSingleSelectionPulldown
  | FormPageInputItemTextSingleLine
  | null => {
  const inputItem = values[addressId];
  if (!inputItem) {
    return null;
  }
  switch (inputItem.inputItemType) {
    case INPUT_ITEM_TYPE.textSingleLine:
      return getPrefectureInputItemTextSingleLine(
        inputItem,
        address.textSingleLine
      );
    case INPUT_ITEM_TYPE.singleSelectionPulldown: {
      const property = properties[addressId];
      return getPrefectureInputItemSingleSelectionPulldown(
        inputItem,
        address.singleSelectionPulldown,
        property
      );
    }
    default:
      return null;
  }
};

type GetUpdateValuesPayload = Pick<
  AutoCompleteAddressPayload,
  'addressData' | 'searchSetting'
> & {
  values: FormPageInputData;
  properties: FormPageProperties;
};

type UpdatedValues = {
  [propertyId: string]: FormPageInputItem | null;
};

const getUpdatedValues = ({
  addressData,
  values,
  searchSetting,
  properties,
}: GetUpdateValuesPayload): UpdatedValues => {
  const {
    prefecture: prefectureId,
    city: cityId,
    town: townId,
  } = searchSetting;
  const { prefecture, city, town } = addressData;

  if (!cityId && !townId) {
    return {
      [prefectureId]: getPrefectureInputItem({
        values,
        addressId: prefectureId,
        properties,
        address: {
          textSingleLine: `${prefecture}${city}${town}`,
          singleSelectionPulldown: prefecture,
        },
      }),
    };
  }
  const prefectureInputItem = getPrefectureInputItem({
    values,
    addressId: prefectureId,
    properties,
    address: {
      textSingleLine: prefecture,
      singleSelectionPulldown: prefecture,
    },
  });
  if (cityId && !townId) {
    return {
      [prefectureId]: prefectureInputItem,
      [cityId]: getAddressTextSingleLineInputItem(
        values,
        `${city}${town}`,
        cityId
      ),
    };
  }
  if (cityId && townId) {
    return {
      [prefectureId]: prefectureInputItem,
      [cityId]: getAddressTextSingleLineInputItem(values, city, cityId),
      [townId]: getAddressTextSingleLineInputItem(values, town, townId),
    };
  }
  // 以下は設定上起こり得ない
  if (!cityId && townId) {
    return {
      [prefectureId]: prefectureInputItem,
      [townId]: getAddressTextSingleLineInputItem(values, town, townId),
    };
  }
  return {};
};

export const autoCompleteAddress = ({
  addressData,
  formikProps,
  searchSetting,
  formPage,
  assortedDrillDownPropertiesGroup,
}: AutoCompleteAddressPayload): void => {
  const { values, setValues } = formikProps;
  if (!formPage?.properties) {
    return;
  }

  const updatedValues = getUpdatedValues({
    addressData,
    values,
    searchSetting,
    properties: formPage.properties as FormPageProperties,
  });

  const filteredUpdatedValues: FormPageInputData = Object.entries(
    updatedValues
  ).reduce((acc, [propertyId, inputItem]) => {
    if (inputItem) {
      return {
        ...acc,
        [propertyId]: inputItem,
      };
    }
    return acc;
  }, {} as FormPageInputData);

  if (!Object.keys(filteredUpdatedValues).length) {
    return;
  }
  setValues((values) => {
    const newValues = {
      ...values,
      ...filteredUpdatedValues,
    };
    if (!assortedDrillDownPropertiesGroup?.length) {
      return newValues;
    }
    return convertValuesForDrillDownFromAssortedPropertiesGroup(
      assortedDrillDownPropertiesGroup,
      newValues
    );
  }, true);
};
